import { useIntl } from "react-intl";

import { Divider } from "@/components/Divider/Divider";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { AdditionalServicesCheckoutOverview } from "@/features/flights/components/additional-services/AdditionalServicesCheckoutOverview";
import { getCityNameByAirportCode } from "@/features/flights/components/additional-services/utils";
import { BaggageAllowance } from "@/features/flights/components/BaggageAllowance";
import { FlightRouteLabel } from "@/features/flights/components/flight-purchase/FlightRouteLabel";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { TravellerDetailsCard } from "@/features/travellers/components/TravellerDetailsCard";
import type { Traveller } from "@/features/travellers/types/types";
import type { GetFlightAvailabilityTokenOutputDataV2 } from "@/types/codegen";

type Props = {
  traveller: Traveller | null;
  flightDetails?: GetFlightAvailabilityTokenOutputDataV2;
  onClose: () => void;
};

export const TravellerDetailsDrawer = ({
  traveller,
  flightDetails,
  onClose,
}: Props) => {
  const intl = useIntl();
  const { getTravellerServices } = useFlightSelectionPayloadContext();

  const routeLabels = [
    getCityNameByAirportCode(
      flightDetails?.flights[0].segments[0].departure.airport.code,
      flightDetails?.flights,
    ),
    getCityNameByAirportCode(
      flightDetails?.flights[0].segments[
        flightDetails?.flights[0].segments.length - 1
      ].arrival.airport.code,
      flightDetails?.flights,
    ),
  ];

  const travellerServices = traveller ? getTravellerServices(traveller.id) : [];
  const departureFlightServices = travellerServices.filter(
    (s) => s.flightDirection === "departure",
  );
  const returnFlightServices = travellerServices.filter(
    (s) => s.flightDirection === "return",
  );

  return (
    <SwipeableDrawer
      hasFullHeight
      heightOffset={24}
      open={traveller !== null}
      onClose={onClose}
      backgroundColor="var(--color-surface-background)"
      fixedHeader={
        <DrawerTopBar
          title="Passenger info and Services"
          onCloseClick={onClose}
        />
      }
    >
      {traveller ? <TravellerDetailsCard traveller={traveller} /> : null}

      <div className="section-card space-y-4">
        <div className="text-lg font-medium text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Baggage info & services",
            description:
              "Title of baggages and services info drawer on flight order summary page",
          })}
        </div>

        <div className="flex flex-col gap-3 rounded-xl border border-stroke-neutral-soft p-4">
          <FlightRouteLabel
            routeLabels={[routeLabels[0], routeLabels[1]]}
            segmentDirection="departure"
          />
          <Divider color="neutral-soft" />
          <BaggageAllowance
            baggageData={flightDetails?.outboundBaggage}
            titleSize="small"
          />
          {departureFlightServices && departureFlightServices.length > 0 ? (
            <div className="mt-3">
              <AdditionalServicesCheckoutOverview
                selectedServices={departureFlightServices}
                services={flightDetails?.optionalServices}
              />
            </div>
          ) : null}
        </div>

        {flightDetails?.flights[1] && flightDetails?.returnBaggage ? (
          <div className="flex flex-col gap-3 rounded-xl border border-stroke-neutral-soft p-4">
            <FlightRouteLabel
              routeLabels={[routeLabels[1], routeLabels[0]]}
              segmentDirection="return"
            />
            <Divider color="neutral-soft" />
            <BaggageAllowance
              baggageData={flightDetails.returnBaggage}
              titleSize="small"
            />
            {returnFlightServices && returnFlightServices.length > 0 ? (
              <div className="mt-3">
                <AdditionalServicesCheckoutOverview
                  selectedServices={returnFlightServices}
                  services={flightDetails?.optionalServices}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </SwipeableDrawer>
  );
};
